import React, { useEffect, useState } from 'react';
import { Alert, Empty, List, Space, Spin, Tag, Typography } from 'antd';
import { fetchOnboardingStatus, requestPresignedUrl } from '../../../services/OnboardingService';
import { useAuth } from '../../../AuthProvider';

const { Text } = Typography;

const statusColor = {
  uploaded: 'processing',
  approved: 'success',
  rejected: 'error',
  under_review: 'warning',
  pending: 'default',
  pending_approval: 'warning',
  expired: 'error',
};

export default function OnboardingDocumentsPanel({ employeeId, token }) {
  const authContext = useAuth();
  const companyId = authContext.additionalUserInfo?.companyId;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!employeeId || !token) return;

    let isMounted = true;
    setLoading(true);
    setError('');

    fetchOnboardingStatus(token, employeeId)
      .then(async (result) => {
        if (!isMounted) return;
        if (!result?.documents?.length) {
          setDocuments([]);
          return;
        }

        const docsWithLinks = await Promise.all(
          result.documents.map(async (doc) => {
            try {
              const presigned = await requestPresignedUrl(token, {
                employeeId,
                companyId,
                documentType: doc.documentType,
                documentName: doc.documentImages?.[0]?.keyName,
                documentImages: doc.documentImages,
                contentType: 'application/octet-stream',
                fileSize: 0,
              });
              if (presigned?.presignedUrl) {
                return {
                  ...doc,
                  documentImages: doc.documentImages?.map((img, i) =>
                    i === 0 ? { ...img, presignedUrl: presigned.presignedUrl } : img
                  ),
                };
              }
            } catch {
              // leave doc as-is if presigned URL fails
            }
            return doc;
          })
        );

        if (isMounted) setDocuments(docsWithLinks);
      })
      .catch(() => {
        if (isMounted) setError('Failed to load onboarding documents.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [employeeId, token, companyId]);

  if (loading) return <Spin size="small" />;
  if (error) return <Alert type="error" showIcon message={error} />;
  if (!documents.length) return <Empty description="No onboarding documents uploaded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <List
      dataSource={documents}
      renderItem={(doc) => {
        const links = [];
        if (Array.isArray(doc.documentImages)) {
          doc.documentImages.forEach((img, i) => {
            const href = img?.presignedUrl || img?.s3Url;
            if (href) {
              links.push({ label: img?.description || img?.keyName || `File ${i + 1}`, href });
            }
          });
        }
        if (!links.length && doc.presignedUrl) {
          links.push({ label: doc.documentName || 'File', href: doc.presignedUrl });
        }

        return (
          <List.Item>
            <List.Item.Meta
              title={
                <Space>
                  <span>{doc.documentName}</span>
                  <Tag color={statusColor[doc.status] || 'default'}>{doc.status}</Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size={2}>
                  <Text type="secondary">{doc.documentType}</Text>
                  {doc.description ? <Text type="secondary">{doc.description}</Text> : null}
                  {links.map((file, idx) => (
                    <a key={idx} href={file.href} target="_blank" rel="noreferrer">
                      {file.label}
                    </a>
                  ))}
                  {doc.rejectionReason ? <Text type="danger">{doc.rejectionReason}</Text> : null}
                </Space>
              }
            />
          </List.Item>
        );
      }}
    />
  );
}
